import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@axilon-apps/libs-ui-shadcn'
import type { DropdownMenuAction } from '@axilon-apps/libs-ui-types'

import MenuButtonThreeDots from '../MenuButtonThreeDots/MenuButtonThreeDots'

interface Props<T> {
  item: T
  menuItems: DropdownMenuAction<T>[]
  /**
   * Hide the entire action column for this row. Defaults to false.
   * Prefer letting the column formatter decide whether to render this
   * component at all — this is here for compatibility with tables that
   * share an identical column config across rows.
   */
  hidden?: boolean
  /**
   * Show a spinner inside the trigger button. Defaults to false.
   * The trigger is also disabled while loading.
   *
   * Kept as `isLoading` alias for legacy callers — both names accepted.
   */
  loading?: boolean
  isLoading?: boolean
}

/**
 * Three-dots dropdown trigger for table action columns.
 *
 * Pairing notes:
 *   1. Set `excludeFromRowClick: true` on this column in your DataTable
 *      ColumnDef. The cell handler stops the click from bubbling to
 *      `onRowClick`. This component also stops propagation defensively
 *      on the trigger itself, so a missing excludeFromRowClick won't
 *      cause the menu trigger to navigate the row.
 *   2. The trigger renders <MenuButtonThreeDots> wrapped in
 *      DropdownMenuTrigger asChild — Radix Slot merges trigger props +
 *      ref through MenuButtonThreeDots → Button → the real <button>.
 *      Both MenuButtonThreeDots and Button are forwardRefs so the ref
 *      reaches the DOM; without that the menu would fail to position.
 */
const TableActionColumnButton = <T,>({
  item,
  menuItems,
  hidden = false,
  loading,
  isLoading
}: Props<T>) => {
  if (hidden || menuItems.length === 0) {
    return null
  }

  const isBusy = loading ?? isLoading ?? false

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <MenuButtonThreeDots
          loading={isBusy}
          // Defence-in-depth: even when the surrounding TableCell forgets
          // to set excludeFromRowClick, the trigger click should never
          // navigate the row that owns this menu.
          onClick={(event) => event.stopPropagation()}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        {menuItems.map((menuItem) => (
          <DropdownMenuItem
            key={menuItem.label}
            onClick={(event) => {
              event.stopPropagation()
              menuItem.onClick(item)
            }}
          >
            {menuItem.icon}
            {menuItem.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TableActionColumnButton
